import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import { MdUpload, MdInfoOutline } from "react-icons/md";
import { IoMdCheckmark } from "react-icons/io";
import { FaRegEdit } from "react-icons/fa";
import { TbCurrentLocation } from "react-icons/tb";
import { IoAddCircleOutline } from "react-icons/io5";
import styles from "../../../styles/__labProfile.module.scss";
import india from "../../../assets/images/flags/Flag_of_India-64x43.png";
import { ChevronDown } from "lucide-react";
import defaultAvatar from "../../../assets/images/default_avatar.png";
import AddressImg from "../../../assets/images/addAddress.png";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { toast } from "../../../components/toast";
import { axiosPrivate } from "../../../api/axios";
import { APP_PATH } from "../../../api/backendPath";
import CharCount from "../../../components/char-count/CharCount";
import { BIO_MAX_LENGTH } from "../../../utils/profileValidation";

// Simple Dialog Component
const DialogReusableMd = ({ open, onClose, children, className }) => {
  useEffect(() => {
    if (open) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className={`${styles["dialog-overlay"]}`}>
      <div className={`${styles["dialog-box"]} ${className || ""}`}>
        <button className={`${styles["dialog-close"]}`} onClick={onClose}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.5525 12.0002L17.8525 7.71019C18.0408 7.52188 18.1466 7.26649 18.1466 7.00019C18.1466 6.73388 18.0408 6.47849 17.8525 6.29019C17.6642 6.10188 17.4088 5.99609 17.1425 5.99609C16.8762 5.99609 16.6208 6.10188 16.4325 6.29019L12.1425 10.5902L7.85252 6.29019C7.66421 6.10188 7.40882 5.99609 7.14252 5.99609C6.87622 5.99609 6.62082 6.10188 6.43252 6.29019C6.24421 6.47849 6.13843 6.73388 6.13843 7.00019C6.13843 7.26649 6.24421 7.52188 6.43252 7.71019L10.7325 12.0002L6.43252 16.2902C6.33879 16.3831 6.26439 16.4937 6.21363 16.6156C6.16286 16.7375 6.13672 16.8682 6.13672 17.0002C6.13672 17.1322 6.16286 17.2629 6.21363 17.3848C6.26439 17.5066 6.33879 17.6172 6.43252 17.7102C6.52548 17.8039 6.63608 17.8783 6.75794 17.9291C6.8798 17.9798 7.01051 18.006 7.14252 18.006C7.27453 18.006 7.40523 17.9798 7.52709 17.9291C7.64895 17.8783 7.75955 17.8039 7.85252 17.7102L12.1425 13.4102L16.4325 17.7102C16.5255 17.8039 16.6361 17.8783 16.7579 17.9291C16.8798 17.9798 17.0105 18.006 17.1425 18.006C17.2745 18.006 17.4052 17.9798 17.5271 17.9291C17.649 17.8783 17.7596 17.8039 17.8525 17.7102C17.9462 17.6172 18.0206 17.5066 18.0714 17.3848C18.1222 17.2629 18.1483 17.1322 18.1483 17.0002C18.1483 16.8682 18.1222 16.7375 18.0714 16.6156C18.0206 16.4937 17.9462 16.3831 17.8525 16.2902L13.5525 12.0002Z"
              fill="currentcolor"
            />
          </svg>
        </button>
        {children}
      </div>
    </div>
  );
};

const WEEK_DAYS = [
  { code: "sun", label: "Sun" },
  { code: "mon", label: "Mon" },
  { code: "tue", label: "Tue" },
  { code: "wed", label: "Wed" },
  { code: "thu", label: "Thu" },
  { code: "fri", label: "Fri" },
  { code: "sat", label: "Sat" },
];
const ALL_DAY_CODES = WEEK_DAYS.map((d) => d.code);

const DEFAULT_START = "08:00";
const DEFAULT_END = "17:00";

// Build a full 7-day schedule from the profile. Prefers the per-day
// `operating_schedule`; falls back to the legacy `operating_days` +
// `operating_hours_start/end` so existing records keep working.
const buildScheduleFromProfile = (profile) => {
  const start = (profile?.operating_hours_start || DEFAULT_START).slice(0, 5);
  const end = (profile?.operating_hours_end || DEFAULT_END).slice(0, 5);
  const savedDays =
    Array.isArray(profile?.operating_days) && profile.operating_days.length > 0
      ? profile.operating_days
      : ALL_DAY_CODES;
  const savedSchedule = Array.isArray(profile?.operating_schedule)
    ? profile.operating_schedule
    : null;

  return WEEK_DAYS.map((d) => {
    if (savedSchedule) {
      const match = savedSchedule.find((s) => s.day === d.code);
      if (match) {
        return {
          day: d.code,
          is_open: !!match.is_open,
          start: (match.start || start).slice(0, 5),
          end: (match.end || end).slice(0, 5),
        };
      }
    }
    return { day: d.code, is_open: savedDays.includes(d.code), start, end };
  });
};

// Derive the legacy fields from the per-day schedule so older consumers
// (patient app, admin panel, etc.) keep working unchanged.
const deriveLegacyFromSchedule = (schedule) => {
  const openDays = schedule.filter((s) => s.is_open);
  const starts = openDays
    .map((s) => s.start)
    .filter(Boolean)
    .sort();
  const ends = openDays
    .map((s) => s.end)
    .filter(Boolean)
    .sort();
  return {
    operating_days: openDays.map((s) => s.day),
    operating_hours_start: starts[0] || DEFAULT_START,
    operating_hours_end: ends[ends.length - 1] || DEFAULT_END,
  };
};

// Maps the stored consultation type to a human-friendly badge label.
const CONSULTATION_LABELS = {
  video: "Onsite",
  online: "Onsite",
  onsite: "Home Visit",
  home_visit: "Home Visit",
  "video-onsite": "Onsite & Home Visit",
  video_onsite: "Onsite & Home Visit",
  both: "Onsite & Home Visit",
};
const getConsultationLabel = (type) => {
  if (!type) return null;
  return CONSULTATION_LABELS[String(type).trim().toLowerCase()] || null;
};

const ImagingProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("+91");
  const [mobileNumber, setMobileNumber] = useState("");
  const [profileImage, setProfileImage] = useState(
    () => sessionStorage.getItem("provider_avatar_url") || defaultAvatar,
  );
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Imaging profile states
  const [imagingProfile, setImagingProfile] = useState(null);
  const [imagingCenterNameEnglish, setImagingCenterNameEnglish] = useState("");
  const [imagingCenterNameArabic, setImagingCenterNameArabic] = useState("");
  // Bilingual "About the imaging center" biography surfaced under Center Information.
  const [imagingBioEnglish, setImagingBioEnglish] = useState("");
  const [imagingBioArabic, setImagingBioArabic] = useState("");

  // ---- Center Information validation (same rules as the doctor profile) ----
  // Both center names are required; each "About the Imaging Center" text is at most
  // BIO_MAX_LENGTH characters. A field's message shows once the user has edited / left it,
  // or tried to save.
  const [touchedFields, setTouchedFields] = useState({});
  // Leaving / re-opening edit mode starts without old messages.
  useEffect(() => {
    if (!isEditing) setTouchedFields({});
  }, [isEditing]);
  const touchField = (field) =>
    setTouchedFields((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  const bioTooLong = (value) =>
    value.trim().length > BIO_MAX_LENGTH
      ? `About the imaging center must be at most ${BIO_MAX_LENGTH} characters`
      : "";
  const profileErrors = {
    imagingCenterNameEnglish: imagingCenterNameEnglish.trim()
      ? ""
      : "Imaging center name (English) is required",
    imagingCenterNameArabic: imagingCenterNameArabic.trim()
      ? ""
      : "Imaging center name (Arabic) is required",
    imagingBioEnglish: bioTooLong(imagingBioEnglish),
    imagingBioArabic: bioTooLong(imagingBioArabic),
  };
  /** The message to show under `field` now ("" = none). */
  const fieldError = (field) =>
    touchedFields[field] ? profileErrors[field] : "";
  /** Red message under a field, linked to it through `${field}-error`. */
  const renderFieldError = (field) =>
    fieldError(field) ? (
      <small
        id={`${field}-error`}
        role="alert"
        className={styles["field-error"]}
      >
        {fieldError(field)}
      </small>
    ) : null;
  /** Props marking a field as invalid (red border, screen-reader state). */
  const invalidProps = (field) =>
    fieldError(field)
      ? {
          className: styles["field-invalid"],
          "aria-invalid": true,
          "aria-describedby": `${field}-error`,
        }
      : {};

  // Per-day operating schedule: [{ day, is_open, start, end }, ...] for all 7 days.
  const [operatingSchedule, setOperatingSchedule] = useState(() =>
    buildScheduleFromProfile(null),
  );

  const toggleDayOpen = (idx) =>
    setOperatingSchedule((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, is_open: !r.is_open } : r)),
    );
  const updateDayTime = (idx, field, value) =>
    setOperatingSchedule((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    );
  // Consultation type (read-only; configured by admin) shown as a badge.
  const [consultationType, setConsultationType] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const fileInputRef = useRef(null);

  // Address modal states
  const [addAddressModal, setAddAddressModal] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);

  // Address form states
  const [addressFormData, setAddressFormData] = useState({
    house_flat_floor_no: "",
    street: "",
    street_arabic: "",
    area_district: "",
    area_district_arabic: "",
    city: "",
    city_arabic: "",
    governorate: "",
    governorate_arabic: "",
    country: "Iraq",
    region_id: "",
  });

  const [addressName, setAddressName] = useState("");
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [isDefaultAddress, setIsDefaultAddress] = useState(false);

  // Map states
  const [mapCenter, setMapCenter] = useState({ lat: 33.3152, lng: 44.3661 }); // Baghdad
  const [markerPosition, setMarkerPosition] = useState(null);
  const [selectedAddressText, setSelectedAddressText] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  // Regions catalog (governorate-level) + the selected region's map bounds, used
  // to bias/restrict the location search to the chosen region.
  const [regions, setRegions] = useState([]);
  const [regionBounds, setRegionBounds] = useState(null);
  // True when the picked location is outside the selected region (shown inline
  // under the search box).
  const [regionMismatch, setRegionMismatch] = useState(false);

  // Refs
  const mapRef = useRef(null);
  const searchInputRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const dropdownRef = useRef(null);

  // Google Maps API
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const [mapError, setMapError] = useState(null);

  const loaderOptions = useMemo(
    () => ({
      googleMapsApiKey: apiKey,
      libraries: ["places", "marker"],
      id: "google-map-script",
    }),
    [apiKey],
  );

  const { isLoaded, loadError } = useJsApiLoader(loaderOptions);

  const countries = [
    { code: "+96", flag: "/flags/Flag_of_Iraq-64x43.png", name: "Iraq" },
    { code: "+91", flag: india, name: "India" },
    { code: "+20", flag: "/flags/Flag_of_Egypt-64x43.png", name: "Egypt" },
    {
      code: "+1",
      flag: "/flags/Flag_of_United_States-64x34.png",
      name: "United States",
    },
  ];

  const selectedCountryData =
    countries.find((country) => country.code === selectedCountry) ||
    countries[0];

  // Create axios instance with auth token
  const createAxiosInstance = () => {
    const token = sessionStorage.getItem("usr_1a2b3c");
    axiosPrivate.defaults.headers.Authorization = `Bearer ${token}`;
    return axiosPrivate;
  };

  // Fetch imaging profile
  const fetchImagingProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get("/api/v1/imaging/profile");

      if (response.data?.data) {
        const profile = response.data.data;
        setImagingProfile(profile);
        setImagingCenterNameEnglish(profile.imaging_center_name_english || "");
        setImagingCenterNameArabic(profile.imaging_center_name_arabic || "");
        setImagingBioEnglish(profile.bio_english || "");
        setImagingBioArabic(profile.bio_arabic || "");
        setOperatingSchedule(buildScheduleFromProfile(profile));
        setConsultationType(profile.consultation_type || null);

        // Set profile image from avatar presigned URL or use default. Keep the
        // shared cache (used by the header) in sync so the avatar stays
        // consistent across the app without reloading on every navigation.
        if (profile.avatar_presigned_url) {
          setProfileImage(profile.avatar_presigned_url);
          sessionStorage.setItem(
            "provider_avatar_url",
            profile.avatar_presigned_url,
          );
        } else {
          setProfileImage(defaultAvatar);
          sessionStorage.removeItem("provider_avatar_url");
        }

        // Parse mobile number to extract country code and number
        if (profile.mobile_number) {
          const mobileStr = profile.mobile_number;
          const country = countries.find((c) => mobileStr.startsWith(c.code));
          if (country) {
            setSelectedCountry(country.code);
            setMobileNumber(mobileStr.substring(country.code.length).trim());
          } else {
            setMobileNumber(mobileStr);
          }
        } else {
          setMobileNumber("");
        }
      }
    } catch (error) {
      console.error("Error fetching imaging profile:", error);
      toast.error("Failed to fetch imaging profile");
    } finally {
      setProfileLoading(false);
    }
  }, []);

  // Save imaging profile
  const handleSaveProfile = async () => {
    // Validate first: show every message under its field and focus the first invalid one.
    const invalidFields = Object.keys(profileErrors).filter(
      (field) => profileErrors[field],
    );
    if (invalidFields.length) {
      setTouchedFields(
        Object.fromEntries(Object.keys(profileErrors).map((f) => [f, true])),
      );
      toast.error(profileErrors[invalidFields[0]]);
      document.getElementById(invalidFields[0])?.focus();
      return;
    }

    try {
      const axiosInstance = createAxiosInstance();

      // Concatenate country code with mobile number
      const fullMobileNumber = mobileNumber
        ? `${selectedCountry}${mobileNumber}`
        : "";

      const payload = {
        imaging_center_name_english: imagingCenterNameEnglish.trim(),
        imaging_center_name_arabic: imagingCenterNameArabic.trim(),
        operating_schedule: operatingSchedule,
        // Keep the legacy fields in sync for backward compatibility.
        ...deriveLegacyFromSchedule(operatingSchedule),
        mobile_number: fullMobileNumber,
        // Send trimmed biographies; empty strings become null on the backend
        // so we don't persist blank "About" values.
        bio_english: imagingBioEnglish.trim(),
        bio_arabic: imagingBioArabic.trim(),
      };

      await axiosInstance.put("/api/v1/imaging/profile", payload);
      toast.success("Profile updated successfully");
      setIsEditing(false);
      await fetchImagingProfile();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.message || "Failed to update profile");
    }
  };

  // Fetch addresses
  const fetchAddresses = useCallback(async () => {
    try {
      setAddressLoading(true);
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get("/api/v1/imaging/addresses");

      if (response.data?.data) {
        setAddresses(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
      toast.error("Failed to fetch addresses");
    } finally {
      setAddressLoading(false);
    }
  }, []);

  // Save address (create or update)
  const handleSaveAddress = async () => {
    try {
      const axiosInstance = createAxiosInstance();

      const payload = {
        name: addressName,
        house_flat_floor_no: addressFormData.house_flat_floor_no,
        street: addressFormData.street,
        street_arabic: addressFormData.street_arabic,
        area_district: addressFormData.area_district,
        area_district_arabic: addressFormData.area_district_arabic,
        city: addressFormData.city,
        city_arabic: addressFormData.city_arabic,
        governorate: addressFormData.governorate,
        governorate_arabic: addressFormData.governorate_arabic,
        country: addressFormData.country,
        region_id: addressFormData.region_id,
        latitude: latitude?.toString(),
        longitude: longitude?.toString(),
        is_default: isDefaultAddress,
      };

      if (editingAddressId) {
        // Update existing address
        await axiosInstance.put(
          `/api/v1/imaging/addresses/${editingAddressId}`,
          payload,
        );
        toast.success("Address updated successfully");
      } else {
        // Create new address
        await axiosInstance.post("/api/v1/imaging/addresses", payload);
        toast.success("Address added successfully");
      }

      await fetchAddresses();
      setAddAddressModal(false);
      resetAddressForm();
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error(error.response?.data?.message || "Failed to save address");
    }
  };

  // Delete address
  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Are you sure you want to delete this address?")) {
      return;
    }

    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.delete(`/api/v1/imaging/addresses/${addressId}`);
      toast.success("Address deleted successfully");
      await fetchAddresses();
    } catch (error) {
      console.error("Error deleting address:", error);
      toast.error("Failed to delete address");
    }
  };

  // Promote an existing address to be the default. Optimistically moves the
  // badge/highlight in place (no reordering); reverts from the server on error.
  // NOTE: send ONLY `{ is_default: true }` — the gateway's update DTO has
  // `forbidNonWhitelisted` on, so spreading the whole address (which carries
  // `id`, `user_id`, `is_active`, `created_at`, `updated_at`) trips a 400
  // "property … should not exist". Every field on the DTO is `@IsOptional()`,
  // so a partial payload is safe — we're only flipping the default flag.
  const handleSetDefaultAddress = async (address) => {
    setAddresses((prev) =>
      prev.map((a) => ({ ...a, is_default: a.id === address.id })),
    );
    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.put(`/api/v1/imaging/addresses/${address.id}`, {
        is_default: true,
      });
      toast.success("Default address updated");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to set default address",
      );
      await fetchAddresses();
    }
  };

  // Reset form
  const resetAddressForm = () => {
    setAddressFormData({
      house_flat_floor_no: "",
      street: "",
      street_arabic: "",
      area_district: "",
      area_district_arabic: "",
      city: "",
      city_arabic: "",
      governorate: "",
      governorate_arabic: "",
      country: "Iraq",
      region_id: "",
    });
    setAddressName("");
    setLatitude(null);
    setLongitude(null);
    setIsDefaultAddress(false);
    setEditingAddressId(null);
    setMarkerPosition(null);
    setSelectedAddressText("");
    setLocationSuggestions([]);
    setLocationSearchQuery("");
    setShowSuggestions(false);
    setRegionBounds(null);
    setRegionMismatch(false);
    sessionTokenRef.current = null;
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  // Edit address
  const handleEditAddress = (address) => {
    setAddressFormData({
      house_flat_floor_no: address.house_flat_floor_no || "",
      street: address.street || "",
      street_arabic: address.street_arabic || "",
      area_district: address.area_district || "",
      area_district_arabic: address.area_district_arabic || "",
      city: address.city || "",
      city_arabic: address.city_arabic || "",
      governorate: address.governorate || "",
      governorate_arabic: address.governorate_arabic || "",
      country: address.country || "Iraq",
      region_id: address.region_id || "",
    });
    setAddressName(address.name || "");
    setLatitude(parseFloat(address.latitude) || null);
    setLongitude(parseFloat(address.longitude) || null);
    setIsDefaultAddress(address.is_default || false);

    if (address.latitude && address.longitude) {
      const lat = parseFloat(address.latitude);
      const lng = parseFloat(address.longitude);
      setMapCenter({ lat, lng });
      setMarkerPosition({ lat, lng });
    }

    setEditingAddressId(address.id);
    setAddAddressModal(true);
  };

  // Handle map click
  const handleMapClick = useCallback(
    (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      // Block picking a point outside the selected region — keep the marker where
      // it was and surface the mismatch (inline + toast).
      if (!isWithinSelectedRegion(event.latLng)) {
        setRegionMismatch(true);
        toast.error(
          "This location is outside the selected region. Please pick an address within the selected region.",
        );
        return;
      }
      setRegionMismatch(false);
      setMarkerPosition({ lat, lng });
      setLatitude(lat);
      setLongitude(lng);

      // Reverse geocode to get address
      if (window.google) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results[0]) {
            setSelectedAddressText(results[0].formatted_address);
            parseAddressComponents(results[0].address_components);
            fetchArabicAddressComponents({ lat, lng });
          }
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [regionBounds, addressFormData.region_id],
  );

  // Marker drag: if dropped outside the selected region, snap it back to its
  // previous position and warn; otherwise accept the new spot and refill.
  const handleMarkerDragEnd = useCallback(
    (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      if (!isWithinSelectedRegion(event.latLng)) {
        setRegionMismatch(true);
        toast.error(
          "This location is outside the selected region. Please pick an address within the selected region.",
        );
        // Snap the marker back to the last valid position (re-render restores it).
        setMarkerPosition((prev) => (prev ? { ...prev } : prev));
        return;
      }
      setRegionMismatch(false);
      setMarkerPosition({ lat, lng });
      setLatitude(lat);
      setLongitude(lng);
      if (window.google) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results[0]) {
            setSelectedAddressText(results[0].formatted_address);
            parseAddressComponents(results[0].address_components);
            fetchArabicAddressComponents({ lat, lng });
          }
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [regionBounds, addressFormData.region_id],
  );

  // Parse address components from Google (English). Merge (don't clobber) so a
  // following async Arabic autofill and the selected region_id/governorate are
  // preserved.
  const parseAddressComponents = (components) => {
    const patch = {};
    components.forEach((component) => {
      const types = component.types;
      if (types.includes("street_number") || types.includes("premise")) {
        patch.house_flat_floor_no = component.long_name;
      }
      if (types.includes("route")) {
        patch.street = component.long_name;
      }
      if (
        types.includes("sublocality") ||
        types.includes("sublocality_level_1") ||
        types.includes("neighborhood")
      ) {
        patch.area_district = component.long_name;
      }
      if (types.includes("locality")) {
        patch.city = component.long_name;
      }
      if (types.includes("administrative_area_level_1")) {
        patch.governorate = component.long_name;
      }
      if (types.includes("country")) {
        patch.country = component.long_name;
      }
    });
    setAddressFormData((prev) => ({ ...prev, ...patch }));
  };

  // The Google Maps JS SDK is loaded in a single language, so to get the Arabic
  // version of the picked location we hit the Geocoding REST API with
  // language=ar and autofill the Arabic address fields. This keeps the address
  // stored in BOTH English and Arabic from one location search. Best-effort: on
  // any failure the English fields are still set.
  const fetchArabicAddressComponents = async ({ placeId, lat, lng }) => {
    if (!apiKey) return;
    const param = placeId
      ? `place_id=${encodeURIComponent(placeId)}`
      : lat != null && lng != null
        ? `latlng=${lat},${lng}`
        : null;
    if (!param) return;
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?${param}&language=ar&key=${apiKey}`,
      );
      const json = await res.json();
      const components = json?.results?.[0]?.address_components;
      if (!components) return;
      const patch = {};
      for (const comp of components) {
        const types = comp.types;
        if (types.includes("route")) {
          patch.street_arabic = comp.long_name;
        } else if (
          types.includes("sublocality") ||
          types.includes("sublocality_level_1")
        ) {
          patch.area_district_arabic = comp.long_name;
        } else if (types.includes("locality")) {
          patch.city_arabic = comp.long_name;
        } else if (types.includes("administrative_area_level_1")) {
          patch.governorate_arabic = comp.long_name;
        }
      }
      if (Object.keys(patch).length > 0) {
        setAddressFormData((prev) => ({ ...prev, ...patch }));
      }
    } catch (e) {
      // Non-fatal — Arabic autofill is best-effort.
    }
  };

  // Returns true if the given point falls within the selected region's map
  // bounds. When no region is selected (or its bounds couldn't be resolved),
  // there's no restriction so it returns true. Uses GEOMETRY (not names) so
  // it's language/spelling-agnostic.
  const isWithinSelectedRegion = (latLng) => {
    if (!addressFormData.region_id || !regionBounds || !window.google)
      return true;
    return regionBounds.contains(latLng);
  };

  // Get current location
  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const latLng = window.google
            ? new window.google.maps.LatLng(lat, lng)
            : null;
          // If a region is selected and the current location falls outside it,
          // reject it (inline + toast) instead of autofilling out-of-region data.
          if (latLng && !isWithinSelectedRegion(latLng)) {
            setRegionMismatch(true);
            toast.error(
              "Your current location is outside the selected region. Please pick an address within the selected region.",
            );
            return;
          }
          setRegionMismatch(false);

          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
          setLatitude(lat);
          setLongitude(lng);

          // Reverse geocode
          if (window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
              if (status === "OK" && results[0]) {
                setSelectedAddressText(results[0].formatted_address);
                parseAddressComponents(results[0].address_components);
                fetchArabicAddressComponents({ lat, lng });
              }
            });
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          toast.error("Failed to get current location");
        },
      );
    } else {
      toast.error("Geolocation is not supported by your browser");
    }
  };

  // Location search state
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Search location using Google Places Autocomplete
  const handleLocationSearch = useCallback(
    (query) => {
      setLocationSearchQuery(query);
      if (!query || query.length < 3 || !window.google) {
        setLocationSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      if (!sessionTokenRef.current) {
        sessionTokenRef.current =
          new window.google.maps.places.AutocompleteSessionToken();
      }

      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: query,
          sessionToken: sessionTokenRef.current,
          componentRestrictions: { country: "iq" },
          // Restrict suggestions to the selected region's bounds (when a region is
          // chosen), so the picked address stays within that governorate.
          ...(regionBounds
            ? { locationRestriction: regionBounds, bounds: regionBounds }
            : {}),
        },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setLocationSuggestions(predictions);
            setShowSuggestions(true);
          } else {
            setLocationSuggestions([]);
            setShowSuggestions(false);
          }
        },
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [regionBounds],
  );

  // Handle suggestion selection — autofill all fields
  const handleSelectSuggestion = useCallback(
    (suggestion) => {
      setLocationSearchQuery(suggestion.description);
      setShowSuggestions(false);
      setLocationSuggestions([]);
      sessionTokenRef.current = null;

      if (!window.google) return;

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: suggestion.place_id }, (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          const latVal = loc.lat();
          const lngVal = loc.lng();

          setMarkerPosition({ lat: latVal, lng: lngVal });
          setMapCenter({ lat: latVal, lng: lngVal });
          setLatitude(latVal);
          setLongitude(lngVal);
          setSelectedAddressText(results[0].formatted_address);
          parseAddressComponents(results[0].address_components);
          // Also autofill the Arabic fields from the same place.
          fetchArabicAddressComponents({
            placeId: suggestion.place_id,
            lat: latVal,
            lng: lngVal,
          });
          // If a region is selected, confirm the picked place is inside it by
          // GEOMETRY (not name matching, which fails for EN/AR/Google spelling
          // differences). If bounds couldn't be resolved, skip rather than warn.
          if (addressFormData.region_id && regionBounds) {
            const inside = regionBounds.contains(loc);
            setRegionMismatch(!inside);
            if (!inside) {
              toast.error(
                "This location is outside the selected region. Please pick an address within the selected region.",
              );
            }
          } else {
            setRegionMismatch(false);
          }
        }
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [regionBounds, addressFormData.region_id],
  );

  // Fetch addresses and profile on mount
  useEffect(() => {
    fetchImagingProfile();
    fetchAddresses();
  }, [fetchImagingProfile, fetchAddresses]);

  // Load the governorate-level regions catalog for the region dropdown.
  useEffect(() => {
    (async () => {
      try {
        const axiosInstance = createAxiosInstance();
        const res = await axiosInstance.get(APP_PATH.regions);
        const list = res?.data?.data || res?.data || [];
        setRegions(
          Array.isArray(list) ? list.filter((r) => r.is_active !== false) : [],
        );
      } catch (e) {
        // Non-fatal — the dropdown just stays empty if regions can't load.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a region is picked: store region_id, prefill the governorate (EN+AR)
  // from the catalog, geocode the region to get its map bounds, and clear any
  // previously autofilled location values so stale data doesn't linger.
  const handleRegionChange = (value) => {
    const id = value ? Number(value) : "";
    const region = regions.find((r) => r.id === Number(value));
    setAddressFormData((prev) => ({
      ...prev,
      region_id: id,
      house_flat_floor_no: "",
      street: "",
      street_arabic: "",
      area_district: "",
      area_district_arabic: "",
      city: "",
      city_arabic: "",
      governorate: region?.region_name_english || "",
      governorate_arabic: region?.region_name_arabic || "",
    }));
    setMarkerPosition(null);
    setLatitude(null);
    setLongitude(null);
    setSelectedAddressText("");
    setLocationSearchQuery("");
    setLocationSuggestions([]);
    setShowSuggestions(false);
    setRegionBounds(null);
    setRegionMismatch(false);
    if (region && window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        {
          address: `${region.region_name_english}, Iraq`,
          componentRestrictions: { country: "iq" },
        },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const g = results[0].geometry;
            const bounds = g.bounds || g.viewport;
            if (bounds) setRegionBounds(bounds);
            if (g.location) {
              setMapCenter({ lat: g.location.lat(), lng: g.location.lng() });
            }
          }
        },
      );
    }
  };

  // Handle map load errors
  useEffect(() => {
    if (loadError) {
      console.error("Google Maps API Error:", loadError);
      setMapError("LOAD_ERROR");
    } else {
      setMapError(null);
    }
  }, [loadError]);

  // Handle file select and upload to server
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validation
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error("File size must be less than 5MB");
      return;
    }

    // Check supported formats
    const supportedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];
    if (!supportedTypes.includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, or WebP image");
      return;
    }

    try {
      setUploadingAvatar(true);

      // Convert file to base64
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Preview immediately (same pattern as the physio app) so the avatar
      // updates instantly without depending on a profile re-fetch / fresh
      // presigned URL from the backend.
      setProfileImage(base64Data);

      // Prepare payload for API
      const payload = {
        file_data: base64Data,
        filename: file.name,
        mime_type: file.type,
        description: "Imaging center profile avatar",
      };

      // Create axios instance with JSON headers
      const axiosInstance = createAxiosInstance();

      // Upload to server
      const response = await axiosInstance.post(APP_PATH.uploadAvatar, payload);

      if (response.data?.status && response.data?.data?.s3_key) {
        const s3Key = response.data.data.s3_key;

        // Update the profile with new avatar URL (use s3_key for permanent storage)
        await updateProfileAvatar(s3Key);

        // Keep the shared header cache in sync and notify the header with the
        // new image directly (mirrors physio) so it updates immediately.
        sessionStorage.setItem("provider_avatar_url", base64Data);
        window.dispatchEvent(
          new CustomEvent("imagingAvatarUpdated", {
            detail: { avatarUrl: base64Data },
          }),
        );

        toast.success("Avatar uploaded successfully");
      } else {
        throw new Error(response.data?.message || "Upload failed");
      }
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error(error.response?.data?.message || "Failed to upload avatar");
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Update profile with new avatar URL
  const updateProfileAvatar = async (s3Key) => {
    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.put("/api/v1/imaging/profile", {
        avatar_url: s3Key, // Store the S3 key, not the presigned URL
      });
    } catch (error) {
      console.error("Error updating profile avatar:", error);
      // Don't show error toast here as the upload was successful
    }
  };

  // Remove image
  const handleRemoveImage = async () => {
    // Optimistically reset the preview (mirrors physio).
    setProfileImage(defaultAvatar);
    if (fileInputRef.current) fileInputRef.current.value = "";

    try {
      setUploadingAvatar(true);

      // Persist removal on the backend
      const axiosInstance = createAxiosInstance();
      await axiosInstance.put("/api/v1/imaging/profile", {
        avatar_url: null,
      });

      // Clear the shared header cache and notify the header to reset, keeping
      // them in sync without depending on a profile re-fetch.
      sessionStorage.removeItem("provider_avatar_url");
      window.dispatchEvent(
        new CustomEvent("imagingAvatarUpdated", {
          detail: { avatarUrl: null, removed: true },
        }),
      );

      toast.success("Avatar removed successfully");
    } catch (error) {
      console.error("Error removing avatar:", error);
      toast.error("Failed to remove avatar");
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <LabDashboardLayout currentPage="earning-payment">
      <div className={styles["labProfile-title"]}>Profile Management</div>

      <div className={styles["labProfile-card"]}>
        <div className={styles["lab-info"]}>
          {/* Header */}
          <div className={styles["lab-info__header"]}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <h2 style={{ margin: 0 }}>Imaging Center Information</h2>
              {getConsultationLabel(consultationType) && (
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: 600,
                    background: "#e6f7f7",
                    color: "#099494",
                    border: "1px solid #099494",
                  }}
                >
                  {getConsultationLabel(consultationType)}
                </span>
              )}
            </div>
            {isEditing ? (
              <div className={styles["btn--wrapper"]}>
                <button
                  className={`${styles["btn"]} ${styles["btn--outline-primary"]}`}
                  onClick={() => {
                    setIsEditing(false);
                    fetchImagingProfile(); // Reset to original values
                  }}
                >
                  Cancel
                </button>

                <button
                  className={`${styles["btn"]} ${styles["btn--primary"]}`}
                  onClick={handleSaveProfile}
                >
                  <IoMdCheckmark size={18} /> Save Changes
                </button>
              </div>
            ) : (
              <button
                className={styles["lab-info__edit-btn"]}
                onClick={() => setIsEditing(!isEditing)}
              >
                <FaRegEdit />
                Edit
              </button>
            )}
          </div>

          {/* Profile Photo */}
          <div className={styles["lab-info__profile"]}>
            <div className={styles["lab-info__avatar"]}>
              <img
                src={profileImage}
                alt="Imaging Center Logo"
                onError={(e) => {
                  // If image fails to load, fallback to default avatar
                  if (e.target.src !== defaultAvatar) {
                    e.target.src = defaultAvatar;
                  }
                }}
              />
            </div>

            <div className={styles["lab-info__photo-actions"]}>
              <button
                className={`${styles["btn"]} ${styles["btn--gray"]}`}
                onClick={handleRemoveImage}
                disabled={uploadingAvatar}
              >
                Remove
              </button>

              <button
                className={`${styles["btn"]} ${styles["btn--primary"]}`}
                onClick={() => fileInputRef.current.click()}
                disabled={uploadingAvatar}
              >
                <MdUpload size={18} />
                {uploadingAvatar ? "Uploading..." : "Upload"}
              </button>

              {/* Hidden File Input */}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageUpload}
              />
            </div>
          </div>

          {/* Guidance banner: encourage a clear, high-resolution profile picture */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              margin: "24px 0",
              padding: "12px 16px",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "10px",
              color: "#1e40af",
              fontSize: "13px",
              lineHeight: 1.5,
            }}
          >
            <MdInfoOutline
              size={20}
              style={{ flexShrink: 0, marginTop: "1px" }}
            />
            <span>
              Please upload a <strong>clear, high-resolution</strong> profile
              picture so patients can easily recognize your imaging center. Use
              a well-lit, sharp image (JPEG, PNG or WebP).
            </span>
          </div>

          {/* Form */}
          <div className={styles["lab-info__form"]}>
            <div className={styles["form-group"]}>
              <label htmlFor="imagingCenterNameEnglish">
                Imaging Center Name (English){" "}
                <span style={{ color: "#dc3545" }}>*</span>
              </label>
              <input
                id="imagingCenterNameEnglish"
                disabled={!isEditing}
                value={imagingCenterNameEnglish}
                onChange={(e) => {
                  setImagingCenterNameEnglish(e.target.value);
                  touchField("imagingCenterNameEnglish");
                }}
                onBlur={() => touchField("imagingCenterNameEnglish")}
                placeholder="Enter imaging center name in English"
                aria-required="true"
                {...invalidProps("imagingCenterNameEnglish")}
              />
              {renderFieldError("imagingCenterNameEnglish")}
            </div>

            <div className={styles["form-group"]}>
              <label htmlFor="imagingCenterNameArabic">
                Imaging Center Name (Arabic){" "}
                <span style={{ color: "#dc3545" }}>*</span>
              </label>
              <input
                id="imagingCenterNameArabic"
                disabled={!isEditing}
                value={imagingCenterNameArabic}
                onChange={(e) => {
                  setImagingCenterNameArabic(e.target.value);
                  touchField("imagingCenterNameArabic");
                }}
                onBlur={() => touchField("imagingCenterNameArabic")}
                placeholder="أدخل اسم مركز التصوير بالعربية"
                dir="rtl"
                aria-required="true"
                {...invalidProps("imagingCenterNameArabic")}
              />
              {renderFieldError("imagingCenterNameArabic")}
            </div>

            {/* Mobile Number — placed here (right after the two name inputs)
                so the 3-column grid renders Name EN + Name AR + Mobile on a
                single row. The About the Imaging Center textareas that follow
                both use `form-group--full` and wrap to their own rows below. */}
            <div className={styles["form-group"]}>
              <label htmlFor="mobile">Mobile Number</label>
              <div
                className={styles["mobile-input-container"]}
                style={{ background: "#f3f4f6", cursor: "not-allowed" }}
              >
                <div
                  className={styles["country-selector"]}
                  style={{ opacity: 0.7 }}
                >
                  <span className={styles["selected-flag"]}>
                    <img src={selectedCountryData?.flag} alt="" />
                    <span className={styles["selected-countryCode"]}>
                      {selectedCountryData.code}
                    </span>
                  </span>

                  <select
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    disabled
                    className={styles["country-dropdown"]}
                    style={{ cursor: "not-allowed" }}
                  >
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.code} {country.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} className={styles["dropdown-arrow"]} />
                </div>

                <input
                  type="tel"
                  id="mobile"
                  placeholder="770 123 4567"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  disabled
                  className={styles["phone-input"]}
                  style={{
                    background: "transparent",
                    color: "#9ca3af",
                    cursor: "not-allowed",
                  }}
                />
              </div>
            </div>

            {/* Textareas inherit the shared `.form-group textarea` styling
                (see __labProfile.module.scss) so the teal focus border,
                border-radius, padding, and disabled treatment match the
                sibling name inputs — no inline overrides needed. */}
            <div
              className={`${styles["form-group"]} ${styles["form-group--full"]}`}
            >
              <label htmlFor="imagingBioEnglish">
                About the Imaging Center (English)
              </label>
              <textarea
                id="imagingBioEnglish"
                disabled={!isEditing}
                value={imagingBioEnglish}
                onChange={(e) => {
                  setImagingBioEnglish(e.target.value);
                  touchField("imagingBioEnglish");
                }}
                onBlur={() => touchField("imagingBioEnglish")}
                placeholder="Write a short introduction about the imaging center, modalities offered, and specialties."
                rows={4}
                maxLength={BIO_MAX_LENGTH}
                {...invalidProps("imagingBioEnglish")}
              />
              <div className={styles["field-meta"]}>
                {renderFieldError("imagingBioEnglish")}
                <CharCount
                  id="imagingBioEnglish-count"
                  value={imagingBioEnglish}
                  max={BIO_MAX_LENGTH}
                />
              </div>
            </div>

            <div
              className={`${styles["form-group"]} ${styles["form-group--full"]}`}
            >
              <label htmlFor="imagingBioArabic">
                About the Imaging Center (Arabic)
              </label>
              <textarea
                id="imagingBioArabic"
                disabled={!isEditing}
                value={imagingBioArabic}
                onChange={(e) => {
                  setImagingBioArabic(e.target.value);
                  touchField("imagingBioArabic");
                }}
                onBlur={() => touchField("imagingBioArabic")}
                placeholder="اكتب نبذة قصيرة عن مركز التصوير والفحوصات المقدمة والتخصصات."
                rows={4}
                dir="rtl"
                maxLength={BIO_MAX_LENGTH}
                {...invalidProps("imagingBioArabic")}
              />
              <div className={styles["field-meta"]}>
                {renderFieldError("imagingBioArabic")}
                <CharCount
                  id="imagingBioArabic-count"
                  value={imagingBioArabic}
                  max={BIO_MAX_LENGTH}
                />
              </div>
            </div>

            {/* Operating hours — each day has its own open/closed state + times */}
            <div className={styles["form-group"]}>
              <label>Operating Hours</label>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  marginTop: "6px",
                }}
              >
                {operatingSchedule.map((row, idx) => {
                  const dayLabel =
                    WEEK_DAYS.find((d) => d.code === row.day)?.label || row.day;
                  return (
                    <div
                      key={row.day}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        disabled={!isEditing}
                        onClick={() => toggleDayOpen(idx)}
                        style={{
                          minWidth: "72px",
                          padding: "6px 10px",
                          borderRadius: "999px",
                          border: `1px solid ${row.is_open ? "#099494" : "#d1d5db"}`,
                          background: row.is_open ? "#099494" : "#fff",
                          color: row.is_open ? "#fff" : "#374151",
                          fontSize: "13px",
                          fontWeight: 600,
                          cursor: isEditing ? "pointer" : "default",
                          opacity: !isEditing && !row.is_open ? 0.5 : 1,
                        }}
                      >
                        {dayLabel}
                      </button>

                      {row.is_open ? (
                        <div className={styles["time-range"]}>
                          <input
                            disabled={!isEditing}
                            type="time"
                            value={row.start}
                            onChange={(e) =>
                              updateDayTime(idx, "start", e.target.value)
                            }
                          />
                          <span>—</span>
                          <input
                            disabled={!isEditing}
                            type="time"
                            value={row.end}
                            onChange={(e) =>
                              updateDayTime(idx, "end", e.target.value)
                            }
                          />
                        </div>
                      ) : (
                        <span style={{ color: "#9ca3af", fontSize: "13px" }}>
                          Closed
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              className={`${styles["form-group"]} ${styles["form-group--full"]}`}
            >
              <label>Imaging Center Address</label>
              <input
                disabled={!isEditing}
                value={
                  addresses.length > 0
                    ? `${addresses[0].house_flat_floor_no}, ${addresses[0].street}, ${addresses[0].area_district}, ${addresses[0].city}, ${addresses[0].governorate}`
                    : "No address added"
                }
                onClick={() => {
                  if (isEditing) {
                    resetAddressForm();
                    setAddAddressModal(true);
                  }
                }}
                style={{ cursor: isEditing ? "pointer" : "default" }}
                readOnly
              />
            </div>
          </div>

          {/* Addresses Section — always visible */}
          <div className={styles["addresses-section"]}>
            <div className={styles["section-header"]}>
              <h3>Addresses</h3>
              <button
                className={styles["add-address-btn"]}
                onClick={() => {
                  resetAddressForm();
                  setAddAddressModal(true);
                }}
              >
                <IoAddCircleOutline /> Add Address
              </button>
            </div>

            {/* Address list — mirrors the physio profile card design
               (see PhysioProfile.jsx). Uses inline styles so the visual
               (teal-highlighted default card, top-right "Default" pill,
               stacked text buttons) stays 1:1 with the physio surface
               without pulling in a separate SCSS variant. */}
            {addressLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "#89899b",
                }}
              >
                Loading addresses...
              </div>
            ) : addresses.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "30px",
                  color: "#89899b",
                }}
              >
                <p>
                  No address added yet. Click &quot;Add Address&quot; to add your
                  center location.
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  marginTop: "16px",
                }}
              >
                {addresses.map((address) => (
                  <div
                    key={address.id}
                    style={{
                      border: address.is_default
                        ? "2px solid #0cbdbd"
                        : "1px solid #e3e3e7",
                      borderRadius: "10px",
                      padding: "14px 18px",
                      position: "relative",
                      background: address.is_default ? "#f2fbfb" : "#fff",
                    }}
                  >
                    {address.is_default && (
                      <span
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "12px",
                          fontSize: "11px",
                          background: "#0cbdbd",
                          color: "#fff",
                          padding: "2px 8px",
                          borderRadius: "10px",
                        }}
                      >
                        Default
                      </span>
                    )}
                    <p style={{ fontWeight: 600, marginBottom: "4px" }}>
                      {address.name || "Center Address"}
                    </p>
                    <p style={{ fontSize: "13px", color: "#666", margin: 0 }}>
                      {[
                        address.house_flat_floor_no,
                        address.street,
                        address.area_district,
                        address.city,
                        address.governorate,
                        address.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                    <div
                      style={{
                        marginTop: "10px",
                        display: "flex",
                        gap: "10px",
                      }}
                    >
                      <button
                        style={{
                          fontSize: "12px",
                          color: "#155DFC",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                        onClick={() => handleEditAddress(address)}
                      >
                        Edit
                      </button>
                      <button
                        style={{
                          fontSize: "12px",
                          color: "#e5252a",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                        onClick={() => handleDeleteAddress(address.id)}
                      >
                        Delete
                      </button>
                      {!address.is_default && (
                        <button
                          style={{
                            fontSize: "12px",
                            color: "#099494",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            fontWeight: 600,
                          }}
                          onClick={() => handleSetDefaultAddress(address)}
                        >
                          Set as Default
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Address Modal */}
      <DialogReusableMd
        className={styles["addMultipleAddressModal"]}
        open={addAddressModal}
        onClose={() => {
          setAddAddressModal(false);
          resetAddressForm();
        }}
      >
        <div className={styles["login-form-container"]}>
          <h2 className={styles["addressTitle"]}>
            {editingAddressId ? "Edit Address" : "Add Address"}
          </h2>

          {/* Map Section */}
          <div className={styles["mapWrapper"]}>
            {!apiKey ? (
              <>
                <img src={AddressImg} alt="Map placeholder" />
                <div
                  style={{
                    padding: "15px",
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffc107",
                    borderRadius: "4px",
                    marginTop: "10px",
                  }}
                >
                  <strong>Google Maps API Key Missing</strong>
                  <p>
                    Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file
                  </p>
                </div>
              </>
            ) : isLoaded ? (
              <>
                <GoogleMap
                  mapContainerStyle={{
                    width: "100%",
                    height: "300px",
                    borderRadius: "8px",
                  }}
                  center={mapCenter}
                  zoom={13}
                  onClick={handleMapClick}
                  onLoad={(map) => (mapRef.current = map)}
                >
                  {markerPosition && (
                    <Marker
                      position={markerPosition}
                      draggable
                      onDragEnd={handleMarkerDragEnd}
                    />
                  )}
                </GoogleMap>

                <button
                  type="button"
                  className={styles["current-location-btn"]}
                  onClick={handleGetCurrentLocation}
                >
                  <TbCurrentLocation /> Use Current Location
                </button>
              </>
            ) : (
              <p>Loading map...</p>
            )}
          </div>

          {/* Address Form */}
          <div className={styles["address-form"]}>
            {/* Location Search */}
            {/* Region — restricts the location search to the selected region */}
            <div className={styles["form-group"]}>
              <label>Region</label>
              <div
                style={{
                  fontSize: "12px",
                  color: "#0f5b5b",
                  background: "#e7fafa",
                  border: "1px solid #cdefef",
                  borderRadius: "6px",
                  padding: "6px 10px",
                  margin: "2px 0 8px",
                }}
              >
                Selecting a region restricts the map search to that area and
                autofills the address fields below. Locations outside the
                selected region aren't allowed.
              </div>
              <select
                value={
                  addressFormData.region_id === ""
                    ? ""
                    : String(addressFormData.region_id)
                }
                onChange={(e) => handleRegionChange(e.target.value)}
                className={styles["form-control"]}
              >
                <option value="">Select a region</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.region_name_english}
                  </option>
                ))}
              </select>
            </div>

            <div
              className={styles["form-group"]}
              style={{ position: "relative" }}
              ref={dropdownRef}
            >
              <label>Search Location</label>
              <input
                type="text"
                value={locationSearchQuery}
                onChange={(e) => handleLocationSearch(e.target.value)}
                onFocus={() =>
                  locationSuggestions.length > 0 && setShowSuggestions(true)
                }
                placeholder="Search for a location to autofill address..."
                className={styles["form-control"]}
                autoComplete="off"
                disabled={!isLoaded}
              />
              {showSuggestions && locationSuggestions.length > 0 && (
                <ul className={styles["location-suggestions"]}>
                  {locationSuggestions.map((s) => (
                    <li
                      key={s.place_id}
                      onClick={() => handleSelectSuggestion(s)}
                      className={styles["suggestion-item"]}
                    >
                      {s.description}
                    </li>
                  ))}
                </ul>
              )}
              {regionMismatch && (
                <span
                  style={{
                    display: "block",
                    marginTop: "6px",
                    color: "#dc2626",
                    fontSize: "13px",
                  }}
                >
                  This location is outside the selected region. Please pick an
                  address within the selected region.
                </span>
              )}
            </div>

            <div className={styles["form-group"]}>
              <label>Address Name</label>
              <input
                type="text"
                value={addressName}
                onChange={(e) => setAddressName(e.target.value)}
                placeholder="e.g., Main Branch, Downtown Center"
                className={styles["form-control"]}
              />
            </div>

            <div className={styles["form-row"]}>
              <div className={styles["form-group"]}>
                <label>
                  House/Flat/Floor No{" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  value={addressFormData.house_flat_floor_no}
                  onChange={(e) =>
                    setAddressFormData({
                      ...addressFormData,
                      house_flat_floor_no: e.target.value,
                    })
                  }
                  className={styles["form-control"]}
                />
              </div>

              <div className={styles["form-group"]}>
                <label>
                  Street (English) <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  value={addressFormData.street}
                  onChange={(e) =>
                    setAddressFormData({
                      ...addressFormData,
                      street: e.target.value,
                    })
                  }
                  className={styles["form-control"]}
                />
              </div>
            </div>

            <div className={styles["form-row"]}>
              <div className={styles["form-group"]}>
                <label>
                  Street (Arabic) <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  value={addressFormData.street_arabic}
                  onChange={(e) =>
                    setAddressFormData({
                      ...addressFormData,
                      street_arabic: e.target.value,
                    })
                  }
                  className={styles["form-control"]}
                  dir="rtl"
                />
              </div>

              <div className={styles["form-group"]}>
                <label>
                  Area/District (English){" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  value={addressFormData.area_district}
                  onChange={(e) =>
                    setAddressFormData({
                      ...addressFormData,
                      area_district: e.target.value,
                    })
                  }
                  className={styles["form-control"]}
                />
              </div>
            </div>

            <div className={styles["form-row"]}>
              <div className={styles["form-group"]}>
                <label>
                  Area/District (Arabic){" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  value={addressFormData.area_district_arabic}
                  onChange={(e) =>
                    setAddressFormData({
                      ...addressFormData,
                      area_district_arabic: e.target.value,
                    })
                  }
                  className={styles["form-control"]}
                  dir="rtl"
                />
              </div>

              <div className={styles["form-group"]}>
                <label>
                  City (English) <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  value={addressFormData.city}
                  onChange={(e) =>
                    setAddressFormData({
                      ...addressFormData,
                      city: e.target.value,
                    })
                  }
                  className={styles["form-control"]}
                />
              </div>
            </div>

            <div className={styles["form-row"]}>
              <div className={styles["form-group"]}>
                <label>
                  City (Arabic) <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  value={addressFormData.city_arabic}
                  onChange={(e) =>
                    setAddressFormData({
                      ...addressFormData,
                      city_arabic: e.target.value,
                    })
                  }
                  className={styles["form-control"]}
                  dir="rtl"
                />
              </div>

              <div className={styles["form-group"]}>
                <label>
                  Governorate (English){" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  value={addressFormData.governorate}
                  onChange={(e) =>
                    setAddressFormData({
                      ...addressFormData,
                      governorate: e.target.value,
                    })
                  }
                  className={styles["form-control"]}
                />
              </div>
            </div>

            <div className={styles["form-row"]}>
              <div className={styles["form-group"]}>
                <label>
                  Governorate (Arabic){" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  value={addressFormData.governorate_arabic}
                  onChange={(e) =>
                    setAddressFormData({
                      ...addressFormData,
                      governorate_arabic: e.target.value,
                    })
                  }
                  className={styles["form-control"]}
                  dir="rtl"
                />
              </div>
            </div>

            <div className={styles["form-group"]}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={isDefaultAddress}
                  onChange={(e) => setIsDefaultAddress(e.target.checked)}
                  style={{
                    width: "18px",
                    height: "18px",
                    cursor: "pointer",
                    accentColor: "#099494",
                  }}
                />
                Set as default address
              </label>
            </div>

            <div className={styles["form-actions"]}>
              <button
                type="button"
                className={styles["btn-cancel"]}
                onClick={() => {
                  setAddAddressModal(false);
                  resetAddressForm();
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles["btn-save"]}
                onClick={handleSaveAddress}
              >
                {editingAddressId ? "Update Address" : "Save Address"}
              </button>
            </div>
          </div>
        </div>
      </DialogReusableMd>
    </LabDashboardLayout>
  );
};

export default ImagingProfile;
